import { chakra } from '@chakra-ui/react';
import {
  createContext,
  forwardRef,
  useContext,
  type ReactNode,
} from 'react';
import { Stack } from './Layout';

type RadioSize = 'sm' | 'md';
const boxSizes: Record<RadioSize, string> = { sm: '4', md: '5' };

interface RadioGroupCtx {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}
const Ctx = createContext<RadioGroupCtx | null>(null);

export interface RadioGroupProps {
  /** Accessible group name — REQUIRED for correct radiogroup semantics. */
  name: string;
  /** Accessible label for the group (aria-label if no visible label is present). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  children?: ReactNode;
}

/** Groups radios: manages shared name/value and exposes radiogroup semantics. */
export function RadioGroup({
  name,
  value,
  onValueChange,
  disabled,
  children,
  ...aria
}: RadioGroupProps) {
  return (
    <Ctx.Provider value={{ name, value, onChange: onValueChange, disabled }}>
      <div role="radiogroup" {...aria}>
        <Stack gap="2">{children}</Stack>
      </div>
    </Ctx.Provider>
  );
}

const Root = chakra('label', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2.5',
    fontFamily: 'body',
    fontSize: 'md',
    color: 'text.primary',
    cursor: 'pointer',
    userSelect: 'none',
    '&:has(input:disabled)': { cursor: 'not-allowed', color: 'text.muted', opacity: 0.7 },
    '& input:checked + span': { opacity: 1 },
  },
});

const Control = chakra('input', {
  base: {
    appearance: 'none',
    boxSize: 'full',
    margin: 0,
    borderWidth: '1px',
    borderColor: 'border.strong',
    borderRadius: 'full',
    bg: 'surface.default',
    cursor: 'inherit',
    transitionProperty: 'background-color, border-color, box-shadow',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    outline: 'none',
    _hover: { borderColor: 'action.primary' },
    _checked: { borderColor: 'action.primary', bg: 'action.primary' },
    _focusVisible: { boxShadow: 'focus', borderColor: 'action.primary' },
    _disabled: { _hover: { borderColor: 'border.strong' } },
  },
});

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'color' | 'onChange'> {
  value: string;
  size?: RadioSize;
  label?: ReactNode;
  onChange?: (value: string) => void;
}

/** A single radio option. Reads shared name/value from a parent `RadioGroup`. */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { value, size = 'md', label, onChange, disabled, checked, name, ...rest },
  ref,
) {
  const group = useContext(Ctx);
  const isChecked = group ? group.value === value : checked;
  const isDisabled = disabled ?? group?.disabled;
  return (
    <Root>
      <chakra.span position="relative" display="inline-flex" boxSize={boxSizes[size]} flexShrink={0}>
        <Control
          ref={ref}
          type="radio"
          name={group?.name ?? name}
          value={value}
          checked={isChecked}
          disabled={isDisabled}
          onChange={() => {
            group?.onChange?.(value);
            onChange?.(value);
          }}
          {...rest}
        />
        <chakra.span
          aria-hidden
          position="absolute"
          inset="0"
          display="grid"
          placeItems="center"
          opacity={0}
          pointerEvents="none"
        >
          <chakra.span boxSize="45%" borderRadius="full" bg="text.inverse" />
        </chakra.span>
      </chakra.span>
      {label != null && <span>{label}</span>}
    </Root>
  );
});
